import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '../assets/logo.png';
import '../styles/tiendapage.css';
import '../styles/tienda.css';
import '../styles/tiendaToolbar.css';
import Filtros from './Filtros';
import Seo from './Seo';
import { useCarrito } from '../context/carritoContext';
import { API_BASE_URL } from '../config/api';
import { getAllProducts } from '../services/catalogApi';
import { tieneVariantes, getVarianteDefault } from '../services/catalogApi';
import { productoPath } from '../utils/slug';

const TiendaPage = () => {
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginaActual, setPaginaActual] = useState(1);
  const productosPorPagina = 25;
  const navigate = useNavigate();
  const location = useLocation();
  const { agregarAlCarrito, carrito } = useCarrito();
  const [agregadoIds, setAgregadoIds] = useState([]);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const filtrosDefault = useMemo(() => ({
    minPrecio: undefined,
    maxPrecio: undefined,
    categoria: '',
    orden: '',
    soloOferta: false,
    tagIds: []
  }), []);

  const [filtros, setFiltros] = useState(filtrosDefault);

  const terminoBusqueda = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('q') || '').trim().toLowerCase();
  }, [location.search]);

  const tagIdDesdeQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('tagId');
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [location.search]);

  const [buscadorTexto, setBuscadorTexto] = useState(terminoBusqueda);
  const debounceRef = useRef(null);

  useEffect(() => {
    setBuscadorTexto((prev) => (prev.trim().toLowerCase() === terminoBusqueda ? prev : terminoBusqueda));
  }, [terminoBusqueda]);

  const contarFiltrosActivos = () => {
    let count = 0;
    if (filtros.minPrecio !== undefined) count += 1;
    if (filtros.maxPrecio !== undefined) count += 1;
    if (filtros.categoria !== '') count += 1;
    if (filtros.orden !== '') count += 1;
    if (filtros.soloOferta === true) count += 1;
    if ((filtros.tagIds || []).length > 0) count += 1;
    return count;
  };

  const obtenerArticulos = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const productos = await getAllProducts(params);
      setArticulos(productos);
      setPaginaActual(1);
    } catch (error) {
      console.error('Error al obtener articulos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const { tagIds, ...filtrosSinTags } = filtros;
    obtenerArticulos(filtrosSinTags);
  }, [filtros, obtenerArticulos]);

  useEffect(() => {
    setFiltros({
      ...filtrosDefault,
      tagIds: tagIdDesdeQuery ? [tagIdDesdeQuery] : []
    });
  }, [terminoBusqueda, tagIdDesdeQuery, filtrosDefault]);

  // Bloquea el scroll del fondo y permite cerrar el modal con Escape
  useEffect(() => {
    if (!mostrarFiltros) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMostrarFiltros(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [mostrarFiltros]);

  const handleFiltrar = (nuevosFiltros) => {
    setFiltros(nuevosFiltros);
    setMostrarFiltros(false);
  };

  const handleBuscarToolbar = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const texto = buscadorTexto.trim();
    navigate(texto ? `/tienda?q=${encodeURIComponent(texto)}` : '/tienda');
  };

  const handleBuscadorChange = (e) => {
    const texto = e.target.value;
    setBuscadorTexto(texto);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const limpio = texto.trim();
      navigate(limpio ? `/tienda?q=${encodeURIComponent(limpio)}` : '/tienda', { replace: true });
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleAgregarCarrito = (e, item, variante) => {
    e.stopPropagation();

    const yaEnCarrito = carrito.some((p) => p.artId === variante.varId);
    if (yaEnCarrito) return;

    const itemCarrito = {
      ...item,
      artId: variante.varId,
      artNom: variante.etiqueta ? `${item.artNom} - ${variante.etiqueta}` : item.artNom,
      artPrecio: variante.artPrecio,
      artDescuento: variante.artDescuento,
      artFoto: variante.artFoto,
    };
    delete itemCarrito.variantes;

    agregarAlCarrito(itemCarrito);
    setAgregadoIds((prev) => [...prev, variante.varId]);

    const evento = new Event('carrito-actualizado');
    window.dispatchEvent(evento);

    setTimeout(() => {
      setAgregadoIds((prev) => prev.filter((id) => id !== variante.varId));
    }, 2000);
  };

  const handleVerOpciones = (e, item) => {
    e.stopPropagation();
    navigate(productoPath(item));
  };

  const handleCardClick = (item) => {
    navigate(productoPath(item));
  };

  const articulosFiltrados = useMemo(() => {
    let data = articulos;

    if ((filtros.tagIds || []).length > 0) {
      data = data.filter((item) => {
        const itemTagIds = (item.tags || []).map((t) => t.tagId ?? t);
        return filtros.tagIds.every((idTag) => itemTagIds.includes(idTag));
      });
    }

    if (terminoBusqueda) {
      data = data.filter((item) => item.artNom?.toLowerCase().includes(terminoBusqueda));
    }

    return data;
  }, [articulos, terminoBusqueda, filtros.tagIds]);

  const totalPaginas = Math.ceil(articulosFiltrados.length / productosPorPagina);
  const indiceInicio = (paginaActual - 1) * productosPorPagina;
  const productosVisibles = articulosFiltrados.slice(indiceInicio, indiceInicio + productosPorPagina);

  const cambiarPagina = (numero) => {
    setPaginaActual(numero);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <>
        <Seo title="Tienda" path="/tienda" />
        <p className="tienda-cargando">Cargando productos...</p>
      </>
    );
  }

  const filtrosActivosCount = contarFiltrosActivos();

  return (
    <section className="tienda-container">
      <Seo
        title={terminoBusqueda ? `Resultados para "${terminoBusqueda}"` : 'Tienda'}
        description="Explora el catálogo completo de CleanZone: papel institucional, limpiadores, desechables y equipo de limpieza con envío en Culiacán."
        path="/tienda"
      />

      <h1 className="tienda-titulo-visual">Tienda CleanZone</h1>

      <div className="tienda-toolbar">
        <button
          type="button"
          className="btn-abrir-filtros"
          onClick={() => setMostrarFiltros(true)}
        >
          Filtros
          {filtrosActivosCount > 0 && (
            <span className="filtros-badge">{filtrosActivosCount}</span>
          )}
        </button>

        <form
          className="tienda-buscador"
          onSubmit={handleBuscarToolbar}
          role="search"
          aria-label="Buscar productos en tienda"
        >
          <input
            type="search"
            value={buscadorTexto}
            onChange={handleBuscadorChange}
            placeholder="Buscar producto..."
            className="tienda-buscador-input"
          />
          <button type="submit" className="tienda-buscador-btn">Buscar</button>
        </form>
      </div>

      <div className="tienda-contenido">
        <div className="tienda-grid">
          {productosVisibles.length === 0 && (
            <p className="tienda-mensaje-vacio">No hay productos disponibles.</p>
          )}

          {productosVisibles.map((item) => {
            const conVariantes = tieneVariantes(item);
            const variante = getVarianteDefault(item);

            if (!variante) return null;

            const foto = variante.artFoto?.[0] ?? variante.artFoto ?? null;
            const srcFoto = foto ? `${API_BASE_URL}${foto}` : '/img/default.jpg';

            const yaEnCarrito = carrito.some((p) => p.artId === variante.varId);
            const mostrarAgregado = agregadoIds.includes(variante.varId) || yaEnCarrito;

            const porcentajeDescuento = variante.artDescuento > 0 && variante.artPrecio > 0
              ? Math.round((variante.artDescuento / variante.artPrecio) * 100)
              : 0;

            return (
              <div
                key={item.artId}
                className="producto-carta tienda-producto"
                onClick={() => handleCardClick(item)}
              >
                <div className="carta-media">
                  {item.artCZ && <img src={Logo} alt="Producto CZ" className="cz-icon" />}
                  {porcentajeDescuento > 0 && (
                    <span className="carta-badge">-{porcentajeDescuento}%</span>
                  )}

                  <img
                    src={srcFoto}
                    alt={item.artNom}
                    className="producto-foto"
                    loading="lazy"
                  />
                </div>

                <div className="producto-info">
                  <h3>{item.artNom}</h3>
                  <div className="precio-accion">
                    <div className="precios">
                      {conVariantes && <span className="precio-desde">Desde</span>}
                      {variante.artDescuento > 0 ? (
                        <p className="precio-linea">
                          <span className="precio-descuento">
                            ${(variante.artPrecio - variante.artDescuento).toFixed(2)}
                          </span>
                          <span className="precio-original">
                            ${variante.artPrecio.toFixed(2)}
                          </span>
                        </p>
                      ) : (
                        <p className="precio-linea">
                          <span className="precio-normal">
                            ${variante.artPrecio.toFixed(2)}
                          </span>
                        </p>
                      )}
                    </div>
                    {conVariantes ? (
                      <button className="button-add compact" onClick={(e) => handleVerOpciones(e, item)}>
                        Ver opciones
                      </button>
                    ) : (
                      <button
                        className={`button-add compact ${mostrarAgregado ? 'is-added' : ''}`}
                        onClick={(e) => handleAgregarCarrito(e, item, variante)}
                        disabled={mostrarAgregado}
                      >
                        {mostrarAgregado ? 'Agregado' : 'Agregar'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {totalPaginas > 1 && (
          <div className="paginacion">
            {Array.from({ length: totalPaginas }, (_, i) => (
              <button
                key={i}
                onClick={() => cambiarPagina(i + 1)}
                className={`paginacion-btn ${paginaActual === i + 1 ? 'activo' : ''}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {mostrarFiltros && (
        <div className="modal-overlay" onClick={() => setMostrarFiltros(false)}>
          <div
            className="modal-filtros"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Filtros de productos"
          >
            <button
              type="button"
              className="modal-cerrar"
              onClick={() => setMostrarFiltros(false)}
              aria-label="Cerrar filtros"
            >
              &times;
            </button>
            <Filtros
              onFiltrar={handleFiltrar}
              filtrosIniciales={filtros}
              mostrarBusqueda={false}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default TiendaPage;
